import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import {
  useGetAccessSession,
  useGetAccessSessionDetails,
  useResetAccessSessionCredentials,
} from '@apono/client-app-sdk';

import AppModal from '@common/components/AppModal';
import { Loader } from '@common/ui/Loader';

import { AccessDetailsTabs } from '@MyAccess/utils/types';
import AccessDetailsTabbed from '@MyAccess/components/AccessDetailsTabbed';
import AccessSessionHeader from '@MyAccess/components/AccessSessionHeader';
import AccessSessionHeaderSkeleton from '@MyAccess/components/AccessSessionHeaderSkeleton';
import { CONNECTION_METHOD_PARAM, SESSION_ID_PARAM } from '@MyAccess/utils/consts';
import CustomAccessDetails from '@MyAccess/components/CustomAccessDetails';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';

const REFETCH_INTERVAL = 2 * 1000;

export default function ConnectionMethodModal() {
  const [queryParams, setQueryParams] = useSearchParams();

  const [confirmReset, setConfirmReset] = useState(false);

  const sessionId = queryParams.get(SESSION_ID_PARAM) || undefined;

  const handleClose = () => {
    if (confirmReset) {
      setConfirmReset(false);
    }

    queryParams.delete(SESSION_ID_PARAM);
    queryParams.delete(CONNECTION_METHOD_PARAM);
    setQueryParams(queryParams);
  };

  const modalTitle = useMemo(() => {
    if (!sessionId) return null;

    if (confirmReset) {
      return <ResetConfirmTitle />;
    }

    return <ConnectionMethodModalTitle sessionId={sessionId} />;
  }, [confirmReset, sessionId]);

  return (
    <AppModal title={modalTitle} open={!!sessionId} closeButton={!confirmReset} onClose={handleClose} width={600}>
      {sessionId && (
        <ConnectionMethodModalContent
          sessionId={sessionId}
          confirmReset={confirmReset}
          onConfirmReset={(val) => setConfirmReset(val)}
        />
      )}
    </AppModal>
  );
}

function ConnectionMethodModalTitle({ sessionId }: { sessionId: string }) {
  const { data: session, isFetched, isError } = useGetAccessSession({ id: sessionId });

  if (!isFetched) {
    return <AccessSessionHeaderSkeleton withArrow={false} />;
  }

  if (isError || !session) {
    return null;
  }

  return (
    <AccessSessionHeader
      icon={session.type.icons.svg}
      name={session.name}
      typeName={session.type.name}
      withArrow={false}
    />
  );
}

function ConnectionMethodModalContent({
  sessionId,
  onConfirmReset,
  confirmReset,
}: {
  sessionId: string;
  confirmReset: boolean;
  onConfirmReset: (val: boolean) => void;
}) {
  const [allowRefetch, setAllowRefetch] = useState(false);

  const {
    data: session,
    isFetched,
    isError,
  } = useGetAccessSession({
    id: sessionId,
    refetch: allowRefetch ? REFETCH_INTERVAL : undefined,
  });

  const isResetting = useMemo(() => session?.credentials?.status.toLowerCase() === 'resetting', [session]);

  useEffect(() => {
    if (isResetting) {
      setAllowRefetch(true);
    } else {
      setAllowRefetch(false);
    }
  }, [session, isResetting]);

  if (!isFetched) {
    return <Loader />;
  }

  if (isError || !session) {
    return <Alert severity="error">No session found</Alert>;
  }

  if (confirmReset && session.credentials?.can_reset) {
    return <ResetConfirmButtons sessionId={session.id} onResolve={() => onConfirmReset(false)} />;
  }

  if (isResetting) {
    return (
      <Loader
        loadingSentences={['This may take a few minutes...', 'Please wait...', 'Working on it...']}
        sx={{ my: 4 }}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <ConnectionMethodModalDetails sessionId={sessionId} />
      {session.credentials?.can_reset && <ResetButton onClick={() => onConfirmReset(true)} />}
    </Stack>
  );
}

function ConnectionMethodModalDetails({ sessionId }: { sessionId: string }) {
  const [queryParams, setQueryParam] = useSearchParams();
  const { data: accessDetails, isFetched: isCredentialsFetched } = useGetAccessSessionDetails({ id: sessionId });
  const { isEnabled: showCustomAccessDetails } = useFlagFixed(Flags.ADMIN_CUSTOM_ACCESS_DETAILS);
  const { track } = useAnalyticsContext();

  if (!isCredentialsFetched) {
    return <Loader />;
  }

  if (!accessDetails) {
    return <Alert severity="error">No access details found</Alert>;
  }

  const activeTab =
    queryParams.get(CONNECTION_METHOD_PARAM) ||
    (accessDetails.json && AccessDetailsTabs.Parameters) ||
    (accessDetails.cli && AccessDetailsTabs.CLI) ||
    AccessDetailsTabs.Instructions;

  const handleTabChange = (newValue: string) => {
    queryParams.set(CONNECTION_METHOD_PARAM, newValue);
    setQueryParam(queryParams);
    track(AnalyticsEvents.AccessDetailsTabClicked, {
      tabName: newValue,
    });
  };

  const isAlreadyViewed = accessDetails?.credentials?.status.toLowerCase() === 'unavailable';
  const instructionsCustomMessage = accessDetails.instructions.custom_message;

  return (
    <Stack spacing={2}>
      {isAlreadyViewed && (
        <Alert severity="warning" variant="outlined">
          You have already viewed these credentials. Reset credentials to view them again.
        </Alert>
      )}
      {showCustomAccessDetails && instructionsCustomMessage && (
        <CustomAccessDetails content={instructionsCustomMessage} title="Message from your admin" />
      )}
      <AccessDetailsTabbed accessDetails={accessDetails} activeTab={activeTab} onTabChange={handleTabChange} />
    </Stack>
  );
}

function ResetButton({ onClick }: { onClick: () => void }) {
  return (
    <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onClick}
        data-trigger={AnalyticsEvents.ResetCredentialsClicked}
      >
        Reset Credentials
      </Button>
    </Stack>
  );
}

function ResetConfirmTitle() {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Are you sure you want to reset credentials?</Typography>
      <Typography variant="body2" color="text.filter">
        You may be logged out of active sessions
      </Typography>
    </Stack>
  );
}

interface ResetConfirmButtonsProps {
  sessionId: string;
  onResolve: () => void;
}

function ResetConfirmButtons({ sessionId, onResolve }: ResetConfirmButtonsProps) {
  const { resetAccessSessionCredentials, isLoading, isSuccess } = useResetAccessSessionCredentials({
    id: sessionId,
  });

  useEffect(() => {
    if (isSuccess) onResolve();
  }, [isSuccess, onResolve]);

  return (
    <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <Button disabled={isLoading} variant="text" color="primary" size="small" onClick={onResolve}>
        No
      </Button>
      <LoadingButton
        loading={isLoading}
        variant="text"
        color="primary"
        size="small"
        onClick={() => resetAccessSessionCredentials()}
      >
        Yes, Reset
      </LoadingButton>
    </Stack>
  );
}
